/* ============================================================
   Beni Quick View — 产品卡片弹窗（方案②：Section Rendering + 专用模板）
   - 打开时 fetch  /products/{handle}?view=quick-view
   - 抽出整个 <product-info>（beni-main-product）注入弹窗 → 变体切换 / 加购 全部走原生
   - 布局不在 section 里分叉：弹窗竖排由本文件 + beni-quick-view.css（.beni-qv 作用域）负责
   - 头部固定文案 "Overview" + 关闭，由外壳渲染
   - 底栏：注入后把真实的 .beni-product__quantity + .beni-product__buy-buttons
     搬进 .beni-qv__footer（仍在 <product-info> 内部，保证变体 id / 加购正常）
   - 注入前先确保片段所需样式已就绪：片段里的 <link> 经 DOMParser 解析后是惰性的，
     注入 DOM 才开始下载 → 会闪一帧无样式（控件丢样式 + 图片被撑大再跳回）。
     样式清单不额外维护，直接从片段里读（片段即数据源），避免与 section 里的资源列表漂移。
   - 卡片 hover / focus / 按下时预热：先取片段、解析出样式表并开始下载，首次打开几乎无需等待
   - 片段里的数量是「请求那一刻」的购物车快照（data-cart-quantity），所以购物车一变
     （cartUpdate）就要把预取缓存连同在途请求一起作废，否则打开的弹窗会沿用旧数量
   API:  window.BeniQuickView.open(productUrl, openerEl) / .close() / .prefetch(productUrl)
   ============================================================ */
(function () {
  if (window.BeniQuickView) return;

  var HEADER_TITLE = 'Overview';
  var CLOSE_ICON =
    '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">' +
    '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  // 加载态：纯 CSS 环形渐变 spinner（复刻 Figma 26612:16816 的角向渐变环，跨浏览器可靠）
  var LOADING_SPINNER = '<span class="beni-qv__spinner" role="status" aria-label="Loading"></span>';

  var modal, overlay, dialog, content, lastOpener, isOpen = false, hideTimer = null, openToken = 0;

  /* ---------- 样式门控 / 片段预取 ---------- */
  var STYLE_WAIT_LIMIT = 4000; // 样式请求异常（被拦截/超时）时不无限等待，宁可降级也不卡在 loading
  var PREFETCH_TTL = 30000; // 预取片段的保鲜期：hover → 点击的窗口足够
  var PREFETCH_MAX = 3; // 最多缓存几个商品，避免内存里堆积 HTML

  var styleReady = {}; // 解析后的绝对 href -> Promise
  var fragmentCache = {}; // url -> { html, hrefs, at }
  var fragmentPending = {}; // url -> { promise, version }
  // 购物车每次变化 +1：片段里的数量快照随之过期，旧版本的缓存与在途请求都不再可用
  var fragmentVersion = 0;

  // 片段里的 href 多为协议相对（//cdn.../a.css?v=123）；link[href] 读回来是绝对地址，
  // 必须统一成绝对地址再比对/做缓存键，否则同一文件会被重复插入、重复下载
  function absoluteUrl(href) {
    try {
      return new URL(href, document.baseURI).href;
    } catch (err) {
      return href;
    }
  }

  // 确保某个样式表已加载并生效（已存在且已生效则直接返回）
  function ensureStylesheet(href) {
    if (!href) return Promise.resolve();
    var key = absoluteUrl(href);
    if (styleReady[key]) return styleReady[key];

    var ready = Promise.resolve().then(function () {
      var link = null;
      var links = document.querySelectorAll('link[rel~="stylesheet"]');
      for (var i = 0; i < links.length; i++) {
        if (links[i].href === key) {
          link = links[i];
          break;
        }
      }
      if (link && link.sheet) return; // 已加载并生效（例如详情页已引入同一文件）

      if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-beni-qv-style', '');
        document.head.appendChild(link);
      }

      return new Promise(function (resolve) {
        var done = function () {
          resolve();
        };
        link.addEventListener('load', done, { once: true });
        link.addEventListener('error', done, { once: true });
      });
    });

    styleReady[key] = Promise.race([
      ready,
      new Promise(function (resolve) {
        setTimeout(resolve, STYLE_WAIT_LIMIT);
      }),
    ]);
    return styleReady[key];
  }

  function ensureStylesheets(hrefs) {
    var seen = {};
    return Promise.all(
      hrefs
        .filter(function (href) {
          var key = href ? absoluteUrl(href) : '';
          if (!key || seen[key]) return false;
          seen[key] = true;
          return true;
        })
        .map(ensureStylesheet)
    );
  }

  // 片段 → { html, hrefs }：hrefs 为该片段自带、注入后才开始下载的样式表
  function parseFragment(text) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var productEl = doc.querySelector('product-info');
    if (!productEl) throw new Error('quick-view product-info not found');

    // 兜底：禁掉整页 swap / 改 URL（模板 template.suffix 分支已设，这里再保险一次）
    productEl.setAttribute('data-update-url', 'false');

    var hrefs = [];
    productEl.querySelectorAll('link[rel~="stylesheet"]').forEach(function (link) {
      hrefs.push(link.getAttribute('href'));
    });

    return { html: productEl.outerHTML, hrefs: hrefs };
  }

  // 购物车变化 → 已缓存的片段（含 data-cart-quantity 旧快照）全部作废；
  // 版本号同时让「更新前发出、更新后才返回」的请求失效，避免旧片段被写回缓存
  function invalidateFragments() {
    fragmentVersion += 1;
    fragmentCache = {};
  }

  // 取片段（已预热则直接用）；同一 url 的并发请求合并
  function loadFragment(url) {
    var cached = fragmentCache[url];
    if (cached && Date.now() - cached.at < PREFETCH_TTL) return Promise.resolve(cached);

    // 购物车更新前发出的请求带的是旧数量快照 → 不能复用
    var pending = fragmentPending[url];
    if (pending && pending.version === fragmentVersion) return pending.promise;

    var version = fragmentVersion;
    var request = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(parseFragment);

    var entry = { promise: request, version: version };
    fragmentPending[url] = entry;
    var release = function () {
      if (fragmentPending[url] === entry) delete fragmentPending[url];
    };
    request.then(release, release);

    return request;
  }

  // hover / focus 预热：片段 + 其自带样式一起提前取
  function prefetch(input) {
    var url = buildUrl(input);
    if (!url) return;

    var cached = fragmentCache[url];
    if (cached && Date.now() - cached.at < PREFETCH_TTL) return;
    var pending = fragmentPending[url];
    if (pending && pending.version === fragmentVersion) return;

    var version = fragmentVersion;
    loadFragment(url)
      .then(function (fragment) {
        if (version !== fragmentVersion) return; // 请求期间购物车已更新 → 不回写旧快照

        ensureStylesheets(fragment.hrefs); // 不等结果，先让请求跑起来
        fragment.at = Date.now();
        fragmentCache[url] = fragment;

        var keys = Object.keys(fragmentCache);
        while (keys.length > PREFETCH_MAX) delete fragmentCache[keys.shift()];
      })
      .catch(function () {
        /* 预热失败无所谓，真正打开时会重新请求 */
      });
  }

  function build() {
    if (modal) return;

    modal = document.createElement('div');
    modal.className = 'beni-qv';
    modal.setAttribute('hidden', '');

    overlay = document.createElement('div');
    overlay.className = 'beni-qv__overlay';
    overlay.setAttribute('data-beni-qv-close', '');

    dialog = document.createElement('div');
    dialog.className = 'beni-qv__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', HEADER_TITLE);
    dialog.setAttribute('tabindex', '-1');

    var header = document.createElement('div');
    header.className = 'beni-qv__header';
    header.innerHTML =
      '<span class="beni-qv__title"></span>' +
      '<button type="button" class="beni-qv__close" data-beni-qv-close aria-label="Close">' +
      CLOSE_ICON +
      '</button>';
    header.querySelector('.beni-qv__title').textContent = HEADER_TITLE;

    content = document.createElement('div');
    content.className = 'beni-qv__content';

    dialog.appendChild(header);
    dialog.appendChild(content);
    modal.appendChild(overlay);
    modal.appendChild(dialog);
    document.body.appendChild(modal);

    // 关闭：遮罩 / 头部关闭按钮
    modal.addEventListener('click', function (evt) {
      if (evt.target.closest('[data-beni-qv-close]')) close();
    });

    document.addEventListener('keydown', function (evt) {
      if (!isOpen) return;
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        evt.stopPropagation();
        close();
      }
    });
  }

  function buildUrl(input) {
    var base = (input || '').split('?')[0];
    if (!base) return '';
    return base + '?view=quick-view';
  }

  // 把真实的数量器 + 加购按钮搬进底栏（仍留在 <product-info> 内部）
  function relocateFooter(root) {
    var beni = root.querySelector('.beni-product');
    if (!beni) return;

    var qty = beni.querySelector('.beni-product__quantity');
    var buy = beni.querySelector('.beni-product__buy-buttons');
    if (!qty && !buy) return;

    var footer = document.createElement('div');
    footer.className = 'beni-qv__footer';
    beni.appendChild(footer); // .beni-product 仍在 product-info 内 → 表单/变体同步不受影响
    if (qty) footer.appendChild(qty);
    if (buy) footer.appendChild(buy);
  }

  function show() {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    modal.removeAttribute('hidden');
    document.documentElement.classList.add('beni-qv-open');
    // 强制 reflow → 触发进场过渡
    // eslint-disable-next-line no-unused-expressions
    modal.offsetHeight;
    modal.classList.add('is-open');
    isOpen = true;

    if (typeof trapFocus === 'function') {
      trapFocus(dialog, dialog);
    } else if (dialog.focus) {
      dialog.focus();
    }
  }

  function close() {
    if (!isOpen) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('beni-qv-open');
    isOpen = false;

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(lastOpener);
    } else if (lastOpener && lastOpener.focus) {
      lastOpener.focus();
    }

    hideTimer = setTimeout(function () {
      modal.setAttribute('hidden', '');
      content.innerHTML = '';
    }, 300);
  }

  function renderState(message) {
    content.innerHTML = '<div class="beni-qv__state">' + message + '</div>';
  }

  function open(input, opener) {
    build();
    var url = buildUrl(input);
    if (!url) return;

    lastOpener = opener || document.activeElement;
    var token = ++openToken;
    renderState(LOADING_SPINNER);
    show();

    loadFragment(url)
      .then(function (fragment) {
        // 片段自带的样式表要先加载生效再注入：DOMParser 解析出的 <link> 是惰性的，
        // 注入 DOM 后浏览器才开始下载 → 首帧无样式（控件丢样式 + 图片被撑大再跳回）
        return ensureStylesheets(fragment.hrefs).then(function () {
          return fragment;
        });
      })
      .then(function (fragment) {
        if (token !== openToken) return; // 等待期间又打开了别的卡片 → 丢弃这次结果

        if (typeof HTMLUpdateUtility !== 'undefined' && HTMLUpdateUtility.setInnerHTML) {
          HTMLUpdateUtility.setInnerHTML(content, fragment.html);
        } else {
          content.innerHTML = fragment.html;
        }

        relocateFooter(content);

        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        if (window.ProductModel && window.ProductModel.loadShopifyXR) window.ProductModel.loadShopifyXR();

        dialog.scrollTop = 0;
        if (dialog.focus) dialog.focus();
      })
      .catch(function (err) {
        console.error('[beni-quick-view]', err);
        if (token === openToken) renderState('Failed to load');
      });
  }

  // 加购 / 改数量成功后：片段里的数量快照已过期 → 作废预取缓存（含在途请求），再关闭弹窗
  // （购物车抽屉 / 通知由 product-form.js 负责打开）
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      invalidateFragments();
      if (isOpen) close();
    });
  }

  // 可选：给未接入自身路由的卡片用的委托触发
  function getDelegatedTrigger(target) {
    return target.closest('[data-beni-qv-trigger], [data-product-addons-quick-view]');
  }

  document.addEventListener('click', function (evt) {
    if (evt.defaultPrevented) return;
    var trigger = getDelegatedTrigger(evt.target);
    if (!trigger) return;
    evt.preventDefault();
    open(trigger.getAttribute('data-product-url') || trigger.getAttribute('href'), trigger);
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.defaultPrevented) return;
    if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;

    var trigger = getDelegatedTrigger(evt.target);
    if (!trigger || trigger !== evt.target) return;

    evt.preventDefault();
    open(trigger.getAttribute('data-product-url') || trigger.getAttribute('href'), trigger);
  });

  /* 卡片 hover / focus 预热（停留 150ms 才发起，避免鼠标扫过网格时逐卡请求）
     触摸端没有 hover，用 pointerdown 抢在 click 之前先发请求 */
  var INTENT_TRIGGER = '[data-quick-view="true"], [data-beni-qv-trigger], [data-product-addons-quick-view]';
  var intentTimer = null;
  var hoveredTrigger = null;

  function canPrefetch() {
    var conn = navigator.connection;
    if (!conn) return true;
    if (conn.saveData) return false; // 用户开了省流量
    return !/(^|-)2g$/.test(conn.effectiveType || '');
  }

  function onIntent(evt) {
    var trigger = evt.target && evt.target.closest ? evt.target.closest(INTENT_TRIGGER) : null;
    if (!trigger) return;

    if (evt.type === 'pointerover') {
      if (trigger === hoveredTrigger) return;
      hoveredTrigger = trigger;
    }

    clearTimeout(intentTimer);

    var url = trigger.getAttribute('data-product-url') || trigger.getAttribute('href');
    if (!canPrefetch()) return;

    if (evt.type === 'pointerdown') {
      prefetch(url); // 按下即发起，click 时通常已在途中
      return;
    }

    intentTimer = setTimeout(function () {
      prefetch(url);
    }, 150);
  }

  document.addEventListener('pointerover', onIntent, true);
  document.addEventListener('pointerdown', onIntent, true);
  document.addEventListener('focusin', onIntent, true);

  window.BeniQuickView = { open: open, close: close, prefetch: prefetch };
})();
